import type { z } from 'zod';

import type { ValidationIssue } from './issues.js';
import type { Registries } from './registries.js';

/**
 * Level one of the dispatch discriminator: what class of document is being
 * validated. Matches Phoenix's asset vocabulary — a `learning_asset` is the
 * envelope-wrapped story/lesson/gameshow/dil document; a `collection` is the
 * curated grouping document.
 */
export const ASSET_KINDS = ['learning_asset', 'collection'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/**
 * Level two of the dispatch discriminator: the closed template-name union the
 * validator owns. Phoenix narrows its nullable `template_name` string to this
 * union at the API boundary. Collections have no template.
 */
export const TEMPLATE_NAMES = ['story', 'lesson', 'gameshow', 'dil'] as const;
export type TemplateName = (typeof TEMPLATE_NAMES)[number];

export const isTemplateName = (value: unknown): value is TemplateName =>
  typeof value === 'string' && (TEMPLATE_NAMES as readonly string[]).includes(value);

export interface RuleContext {
  /** Injected registry data — always present; rules never skip for want of it. */
  registries: Registries;
}

export interface Rule<T> {
  code: string;
  // Method syntax (not an arrow property) is deliberate: it keeps the input
  // parameter bivariant, so a Rule<Envelope> slots into a Rule<unknown> list.
  // eslint-disable-next-line ts/method-signature-style
  check(input: T, ctx: RuleContext): ValidationIssue[];
}

/**
 * Minimal inner shape shared by every template's `storyData` (story, lesson,
 * gameshow, dil): a `nodes` array. Per-activity setup-shape rules only
 * traverse `nodes[].activity`, so they are typed against this container rather
 * than a specific template — letting the same rule run on stories and lessons
 * alike.
 */
export interface ActivityNodeContainer {
  nodes: unknown[];
}

/**
 * One validatable document type: a (kind, templateName) address, the zod
 * schema for the full document, and the rules that run when the schema parse
 * succeeds. Collections carry no templateName.
 */
export interface AssetType<T> {
  kind: AssetKind;
  /** Required for learning assets; absent for collections. */
  templateName?: TemplateName;
  schema: z.ZodType<T>;
  rules: Rule<T>[];
}
